'use client';
import React, { useState, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import {
  AlertTriangle,
  Radio,
  Users,
  Calendar,
  FolderOpen,
  BarChart2,
  Building2,
  Shield,
  Activity,
  Lock,
  UserPlus,
  Sparkles,
  PhoneCall,
  Archive,
  FileText,
  LayoutGrid,
  Car,
  Truck,
} from 'lucide-react';

const DESKTOP_APPS = [
  {
    id: 'dispatch',
    label: 'Dispatch',
    sublabel: 'Meldkamer',
    icon: AlertTriangle,
    route: '/dispatch-meldkamer',
    color: '#ef4444',
    description: 'Incidenten beheren & eenheden inzetten',
  },
  {
    id: 'uitvraag-centralist',
    label: 'Uitvraag',
    sublabel: 'Centralist',
    icon: PhoneCall,
    route: '/uitvraag-centralist',
    color: '#00ccff',
    description: 'Uitvragen beheren & doorverbinden',
  },
  {
    id: 'communicatie',
    label: 'Communicatie',
    sublabel: 'Chat & Broadcast',
    icon: Radio,
    route: '/communicatie',
    color: '#0ea5e9',
    description: 'Interne chat en broadcast berichten',
  },
  {
    id: 'post-overzicht',
    label: 'Post Overzicht',
    sublabel: 'Wie doet wat',
    icon: LayoutGrid,
    route: '/post-overzicht',
    color: '#f59e0b',
    description: 'Live kleurgecodeerd overzicht van meldkamer posten',
  },
  {
    id: 'hr',
    label: 'HR',
    sublabel: 'Personeel',
    icon: Users,
    route: '/hr',
    color: '#8b5cf6',
    description: 'Medewerkers en rollen beheren',
  },
  {
    id: 'planning',
    label: 'Planning',
    sublabel: 'Shifts & Roosters',
    icon: Calendar,
    route: '/planning',
    color: '#10b981',
    description: 'Shifts plannen en personeel inzetten',
  },
  {
    id: 'documenten',
    label: 'Documenten',
    sublabel: 'Bestanden',
    icon: FolderOpen,
    route: '/documenten',
    color: '#f59e0b',
    description: 'Briefings, rapporten en templates',
  },
  {
    id: 'overview',
    label: 'Overview',
    sublabel: 'Event Dashboard',
    icon: BarChart2,
    route: '/overview-event-dashboard',
    color: '#38bdf8',
    description: 'Live event statistieken en KPIs',
  },
  {
    id: 'evenementen',
    label: 'Evenementen',
    sublabel: 'Afdeling',
    icon: Building2,
    route: '/evenementen-afdeling',
    color: '#ec4899',
    description: 'Events aanmaken en beheren',
  },
  {
    id: 'archief',
    label: 'Archief',
    sublabel: 'Historisch Overzicht',
    icon: Archive,
    route: '/archief',
    color: '#f59e0b',
    description: 'Historische evenementen en tijdlijnen',
  },
  {
    id: 'incident-rapportage',
    label: 'Rapportage',
    sublabel: 'Incident Rapport',
    icon: FileText,
    route: '/incident-rapportage',
    color: '#ef4444',
    description: 'Incident statistieken en afdrukbare rapporten',
  },
  {
    id: 'account-aanmaken',
    label: 'Account',
    sublabel: 'Aanmaken',
    icon: UserPlus,
    route: '/account-aanmaken',
    color: '#a78bfa',
    description: 'Nieuw medewerker account aanmaken',
  },
  {
    id: 'schoonmaak',
    label: 'Schoonmaak',
    sublabel: 'Taken & Protocollen',
    icon: Sparkles,
    route: '/schoonmaak',
    color: '#10b981',
    description: 'Schoonmaaktaken en protocollen beheren',
  },
  {
    id: 'wagenpark',
    label: 'Wagenpark',
    sublabel: 'Voertuigen',
    icon: Truck,
    route: '/wagenpark',
    color: '#0ea5e9',
    description: 'Voertuigen beheren en registreren',
  },
  {
    id: 'in-de-wagen',
    label: 'In de Wagen',
    sublabel: 'Status & Checklist',
    icon: Car,
    route: '/in-de-wagen',
    color: '#f59e0b',
    description: 'Voertuigstatus, evenement en checklists',
  },
];

export default function DesktopHome() {
  const router = useRouter();
  const [time, setTime] = useState('');
  const [date, setDate] = useState('');
  const [hoveredApp, setHoveredApp] = useState<string | null>(null);
  const [hoveredLock, setHoveredLock] = useState(false);

  useEffect(() => {
    const update = () => {
      const now = new Date();
      setTime(now.toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false }));
      setDate(now.toLocaleDateString('nl-NL', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }));
    };
    update();
    const interval = setInterval(update, 1000);
    return () => clearInterval(interval);
  }, []);

  const handleAppClick = (route: string) => {
    if (route !== '#') router.push(route);
  };

  const handleLock = () => {
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('watchtower:lock'));
    }
  };

  return (
    <div className="relative w-full h-full flex flex-col overflow-hidden select-none">
      {/* Animated background grid */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage: `
            linear-gradient(rgba(14,165,233,0.03) 1px, transparent 1px),
            linear-gradient(90deg, rgba(14,165,233,0.03) 1px, transparent 1px)
          `,
          backgroundSize: '48px 48px',
        }}
      />

      {/* Radial glow center */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background: 'radial-gradient(ellipse 70% 50% at 50% 40%, rgba(14,165,233,0.06) 0%, transparent 70%)',
        }}
      />

      {/* Corner accent glows */}
      <div className="absolute top-0 left-0 w-64 h-64 pointer-events-none" style={{ background: 'radial-gradient(circle at 0% 0%, rgba(14,165,233,0.08) 0%, transparent 60%)' }} />
      <div className="absolute bottom-0 right-0 w-64 h-64 pointer-events-none" style={{ background: 'radial-gradient(circle at 100% 100%, rgba(236,72,153,0.06) 0%, transparent 60%)' }} />

      {/* Lock PC button — top right corner of desktop */}
      <button
        onClick={handleLock}
        onMouseEnter={() => setHoveredLock(true)}
        onMouseLeave={() => setHoveredLock(false)}
        className="absolute top-4 right-4 z-20 flex items-center gap-2 px-3 py-2 rounded-xl transition-all duration-200 focus:outline-none"
        style={{
          background: hoveredLock ? 'rgba(239,68,68,0.12)' : 'rgba(255,255,255,0.03)',
          border: `1px solid ${hoveredLock ? 'rgba(239,68,68,0.35)' : 'rgba(255,255,255,0.07)'}`,
          boxShadow: hoveredLock ? '0 4px 16px rgba(239,68,68,0.15)' : 'none',
          transform: hoveredLock ? 'translateY(-1px)' : 'none',
        }}
        title="PC vergrendelen"
      >
        <Lock
          size={14}
          style={{
            color: hoveredLock ? '#ef4444' : '#6b7a8d',
            filter: hoveredLock ? 'drop-shadow(0 0 6px rgba(239,68,68,0.6))' : 'none',
            transition: 'all 200ms ease',
          }}
        />
        <span
          className="text-xs font-medium transition-colors duration-200"
          style={{ color: hoveredLock ? '#ef4444' : '#6b7a8d' }}
        >
          Vergrendelen
        </span>
      </button>

      {/* Main content */}
      <div className="relative z-10 flex flex-col items-center justify-center h-full gap-8 px-8 py-6">

        {/* Clock + Date */}
        <div className="text-center">
          <div
            className="font-mono text-5xl font-medium tabular-nums tracking-widest mb-1"
            style={{ color: '#0ea5e9', textShadow: '0 0 30px rgba(14,165,233,0.4)' }}
          >
            {time || '00:00:00'}
          </div>
          <div className="text-sm text-wt-muted capitalize tracking-wide">{date}</div>
        </div>

        {/* Status bar */}
        <div
          className="flex items-center gap-6 px-6 py-3 rounded-xl border"
          style={{
            background: 'rgba(14,165,233,0.04)',
            borderColor: 'rgba(14,165,233,0.15)',
            backdropFilter: 'blur(8px)',
          }}
        >
          <div className="flex items-center gap-2">
            <Shield size={13} className="text-wt-primary" />
            <span className="text-xs font-medium text-wt-muted uppercase tracking-widest">WATCHTOWER</span>
          </div>
          <div className="w-px h-4 bg-wt-border" />
          <div className="flex items-center gap-1.5">
            <Activity size={11} className="text-green-400" />
            <span className="text-xs text-green-400 font-medium">Systeem Online</span>
          </div>
        </div>

        {/* App Icons Grid */}
        <div className="grid grid-cols-7 gap-4">
          {DESKTOP_APPS.map((app) => {
            const isHovered = hoveredApp === app.id;
            return (
              <button
                key={app.id}
                onClick={() => handleAppClick(app.route)}
                onMouseEnter={() => setHoveredApp(app.id)}
                onMouseLeave={() => setHoveredApp(null)}
                className="group flex flex-col items-center gap-3 p-4 rounded-2xl transition-all duration-200 focus:outline-none"
                style={{
                  background: isHovered ? `${app.color}12` : 'rgba(255,255,255,0.02)',
                  border: `1px solid ${isHovered ? `${app.color}35` : 'rgba(255,255,255,0.05)'}`,
                  transform: isHovered ? 'translateY(-4px) scale(1.04)' : 'translateY(0) scale(1)',
                  boxShadow: isHovered ? `0 12px 32px ${app.color}20, 0 0 0 1px ${app.color}20` : 'none',
                  minWidth: '100px',
                }}
              >
                {/* Icon container */}
                <div
                  className="w-16 h-16 rounded-2xl flex items-center justify-center transition-all duration-200"
                  style={{
                    background: isHovered
                      ? `linear-gradient(135deg, ${app.color}25 0%, ${app.color}10 100%)`
                      : `${app.color}15`,
                    border: `1px solid ${isHovered ? `${app.color}50` : `${app.color}25`}`,
                    boxShadow: isHovered ? `0 0 20px ${app.color}30, inset 0 1px 0 ${app.color}20` : 'none',
                  }}
                >
                  <app.icon
                    size={30}
                    style={{
                      color: app.color,
                      filter: isHovered ? `drop-shadow(0 0 8px ${app.color}80)` : 'none',
                      transition: 'all 200ms ease',
                    }}
                  />
                </div>

                {/* Label */}
                <div className="text-center">
                  <div
                    className="text-sm font-semibold leading-tight transition-colors duration-200 wt-desktop-app-label"
                    style={{ color: isHovered ? app.color : '#c8d8e8' }}
                  >
                    {app.label}
                  </div>
                  <div className="text-xs text-wt-muted mt-0.5 leading-tight">{app.sublabel}</div>
                </div>

                {/* Tooltip description */}
                {isHovered && (
                  <div
                    className="absolute mt-1 px-2 py-1 rounded-lg text-xs text-wt-muted text-center max-w-[110px] leading-snug pointer-events-none wt-desktop-tooltip"
                    style={{
                      background: 'rgba(7,12,20,0.95)',
                      border: '1px solid rgba(255,255,255,0.08)',
                      transform: 'translateY(72px)',
                      zIndex: 50,
                      whiteSpace: 'normal',
                    }}
                  >
                    {app.description}
                  </div>
                )}
              </button>
            );
          })}
        </div>

        {/* Bottom hint */}
        <p className="text-xs text-wt-muted/50 tracking-widest uppercase">
          Klik op een app om te openen
        </p>
      </div>
    </div>
  );
}
